import { AxiosError } from 'axios';
import React, { useContext, useEffect, useState, VFC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { DataLayerContext } from '@/components/common/DataLayerProviderContainer';
import Modal from '@/components/common/Modal';
import HeaderTitle from '@/components/common/Title/HeaderTitle';
import { SectionUpload, TFileProps } from '@/components/Mypage/SelfResume/SectionUpload';
import { ModalEntry } from '@/components/Mypage/SelfResume/ModalEntry';
import { TitleSectionTitle } from '@/components/common/Title/SectionTitle';
import { NotificationMatchbou } from '@/components/common/Notification/Matchbou';
import BaseButton from '@/components/common/Button/BaseButton';
import { Wrapper } from '@/components/common/Page';
import { notificationError, notificationSuccess, toggleLoading, userInfo } from '@/redux/index';
import { State } from '@/redux/state';
import {
  MatchboxUserResponse,
  ResponseError,
  UserApi,
  InputAgencyResponse,
} from '@/utils/api-client/api';

/**
 * # マイページ /mypage/self-resume-cv/edit
 * https://docs.google.com/presentation/d/1occFnVVBCMIQ0ftZiWFYGkVwEUKYgetIdp7-sE_pUKA/edit?usp=sharing
 */
export const SelfResumeCv: VFC = () => {
  // ====================================================================================================
  //
  // HOOKS
  //
  // ====================================================================================================
  const { push } = useContext(DataLayerContext);

  // ====================================================================================================
  //
  // REDUX
  //
  // ====================================================================================================
  const user = useSelector((state: State) => state.user);
  const dispatch = useDispatch();

  // ====================================================================================================
  //
  // seuState
  //
  // ====================================================================================================
  const [userData, setUser] = useState<MatchboxUserResponse>();
  const [resume, setResume] = useState<TFileProps | null>(null);
  const [career, setCareer] = useState<TFileProps | null>(null);
  const [fileData, setFileData] = useState<boolean>(false);
  const [modal, setModal] = useState<boolean>(false);
  const [modalNumber, setModalNumber] = useState<number>(0);
  const [modalDelete, setModalDelete] = useState<boolean>(false);
  const [deleteType, setDeleteType] = useState<'resume' | 'career'>();
  const [inputAgencyStatus, setInputAgencyStatus] = useState<InputAgencyResponse>();
  const [notificationMatchbouShow, setNotificationMatchbouShow] = useState(false);

  // ====================================================================================================
  //
  // METHOD
  //
  // ====================================================================================================
  const uploadDocument = async (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    if (!e.target.files?.length) return;
    const imageFile = e.target.files[0];
    const reader = new FileReader();
    reader.readAsBinaryString(imageFile);
    const formData = new FormData();
    formData.append('files', imageFile);
    sendDocument(formData.get('files') as string, name);
    e.target.value = '';
  };

  const updateResumeCareer = () => {
    if (!userData?.resume) {
      setResume(null);
    } else {
      setResume({
        date: userData?.resume?.date,
        name: userData?.resume?.name,
        size: userData?.resume?.size,
        pdfDl: userData?.resume?.url,
      });
    }
    if (!userData?.career_history) {
      setCareer(null);
    } else {
      setCareer({
        date: userData?.career_history?.date,
        name: userData?.career_history?.name,
        size: userData?.career_history?.size,
        pdfDl: userData?.career_history?.url,
      });
    }
  };

  const clickDeleteConfirm = (type: 'resume' | 'career') => {
    if (inputAgencyStatus?.status === 1) {
      dispatch(
        notificationError(
          '入力代行を依頼中のため書類をアップロードおよび削除することができません。'
        )
      );
    } else {
      setDeleteType(type);
      setModalDelete(true);
    }
  };

  // ====================================================================================================
  //
  // API
  //
  // ====================================================================================================
  const deleteDocument = async () => {
    const id = deleteType === 'resume' ? userData?.resume?.f_id : userData?.career_history?.f_id;
    if (id) {
      dispatch(toggleLoading(true));
      new UserApi()
        .deleteDocuments(id)
        .then(() => {
          dispatch(notificationSuccess('書類の削除が完了しました。'));
          getUser();
        })
        .catch((error: AxiosError<ResponseError>) => {
          if (error.response) dispatch(notificationError(error.response.data.error_message));
        })
        .finally(() => {
          setModalDelete(false);
          setDeleteType(undefined);
          dispatch(toggleLoading(false));
        });
    }
  };

  const sendDocument = async (files: string, type: string) => {
    dispatch(toggleLoading(true));
    new UserApi()
      .postDocuments(files, type)
      .then(() => {
        push({
          event: 'fileUpload',
          actionType: 'file_upload',
          actionName: type === 'resume' ? '履歴書' : '職務経歴書',
        });
        dispatch(notificationSuccess('書類のアップロードが完了しました。'));
        getUser();
      })
      .catch((error: AxiosError<ResponseError>) => {
        if (error.response) {
          if (
            error.response.data.error_message
              .toString()
              .indexOf('容量制限の3MBを超えているようです') > -1
          ) {
            dispatch(
              notificationError(
                '容量制限の3MBを超えているようです、画像ファイルの容量をお確かめの上もう一度アップロードしてください。'
              )
            );
          } else {
            dispatch(notificationError(error.response.data.error_message));
          }
        }
      })
      .finally(() => {
        dispatch(toggleLoading(false));
      });
  };

  const getUser = () => {
    (async () => {
      const user = await new UserApi().getUser();
      if (user.status === 200) {
        setUser(user.data);
        console.log(user.data);
      }
    })();
  };

  const getInputAgency = () => {
    (async () => {
      const input_agency = await new UserApi().getInputAgency();
      if (input_agency.status === 200) {
        setInputAgencyStatus(input_agency.data);
        if (input_agency.data.status == 1) {
          setNotificationMatchbouShow(true);
        }
      }
    })();
  };

  const clickEntry = () => {
    if (localStorage.getItem('InputAgencyExplanation') === user?.matchbox_id) {
      setModalNumber(3);
    } else {
      localStorage.setItem('InputAgencyExplanation', user?.matchbox_id ? user?.matchbox_id : '');
    }
    setModal(true);
  };

  // ====================================================================================================
  //
  // useEffect
  //
  // ====================================================================================================
  useEffect(() => {
    if (userData?.user_info) {
      dispatch(userInfo(userData?.user_info));
    }
  }, [userData?.user_info?.matchbox_id]);

  useEffect(() => {
    updateResumeCareer();
    if (userData?.career_history !== null || userData.resume !== null) {
      setFileData(true);
    } else {
      setFileData(false);
    }
  }, [userData]);

  useEffect(() => {
    try {
      dispatch(toggleLoading(true));
      getUser();
      getInputAgency();
    } catch (error) {
      const e = error as AxiosError<ResponseError>;
      if (e.response) dispatch(notificationError(e.response.data.error_message));
    } finally {
      dispatch(toggleLoading(false));
    }
  }, []);

  // ====================================================================================================
  //
  // JSX
  //
  // ====================================================================================================
  return (
    <Wrapper
      header={
        <div className="sticky top-0 z-10">
          <HeaderTitle title={'自作の履歴書・職務経歴書の登録'}></HeaderTitle>
        </div>
      }
    >
      <div className="mb-100 sm:mb-188">
        <div className="sticky top-64 z-20 mt-16 pb-20 sm:pb-32">
          <NotificationMatchbou
            digit={0}
            show={notificationMatchbouShow}
            onClick={() => setNotificationMatchbouShow(false)}
            showMatchbou={false}
          >
            MATCHBOX事務局に入力代行を依頼中です。回答をお待ちください。
          </NotificationMatchbou>
        </div>

        <div className="w-624 m-auto sm:w-full">
          <p className="mbx-typography--body_1 text-center mt-48 sm:mt-24">
            自作の履歴書・職務経歴書をお持ちの場合、
            <br />
            登録（アップロード）することでマイナビワークスのキャリアアドバイザーに
            <br />
            見せることができ、企業へのアピールにつながります。
          </p>
          <section>
            <TitleSectionTitle>
              <div className="mt-48 flex items-center">
                <span className="mr-4">自作の履歴書・職務経歴書の登録</span>
              </div>
            </TitleSectionTitle>
            <p className="text-14_21">
              自分で作成した履歴書・職務経歴書を登録（アップロード）することができます。
              <br />
              MATCHBOXにご自身の経歴を入力することで、直接的に企業へアピールすることができます。加えて、自作の履歴書・職務経歴書を登録（アップロード）すれば、より多くのオファーを受け取るために、キャリアアドバイザーが内容を確認の上、間接的に企業へアピールすることも可能になります。
            </p>
          </section>

          <div>
            <SectionUpload
              upload={(event) => uploadDocument(event, 'resume')}
              data={resume}
              type="resume"
              clickDeleteConfirm={() => clickDeleteConfirm('resume')}
              disabled={inputAgencyStatus?.status === 1}
            />
            <SectionUpload
              upload={(event) => uploadDocument(event, 'career')}
              data={career}
              type="career"
              clickDeleteConfirm={() => clickDeleteConfirm('career')}
              disabled={inputAgencyStatus?.status === 1}
            />
          </div>

          <p className="mbx-typography--caption_1 mt-32">
            ※
            ご登録いただいた履歴書・職務経歴書はオファー機能登録時、経験やスキルに関する情報に限り、マイナビワークスのキャリアアドバイザーが企業へのアピールに使用します。なお、ご自身でブロックした企業へは開示されることはありません。
            <br />※ Text、Excel、Word、PowerPoint、PDF、JPG (各ファイル・合計ともに 3 MB 以内)
          </p>

          <section className="bg-gray-50 mt-56 sm:px-24 py-40 px-40 mx-auto">
            <p className="mbx-typography--heading_3 text-center">
              MATCHBOXへの入力代行サービスのご案内
            </p>
            <p className="text-14_21 mt-32">
              登録（アップロード）した履歴書・職務経歴書は、そのまま企業に開示することはありません。したがって直接的に企業にアピールするためには、MATCHBOX画面への入力が必要です。
            </p>
            <p className="text-14_21 mt-16">
              そこで、入力の手間を省くために、MATCHBOXの会員様は
              <b className="font-bold">入力代行サービス</b>
              を利用することができます。自作の履歴書・職務経歴書を上記で登録（アップロード）した上で、
              <b className="font-bold">入力代行サービス</b>
              をご希望いただくと、MATCHBOX事務局がMATCHBOXのフォーマットに合わせて入力代行を行います。
            </p>
            <div className="text-center w-300 sm:w-full mx-auto">
              <BaseButton
                theme={'primary'}
                size="m"
                className="mt-32 w-full"
                width="large"
                onClick={() => {
                  clickEntry();
                }}
              >
                申し込む
              </BaseButton>
            </div>
          </section>
        </div>

        {typeof inputAgencyStatus?.status === 'number' && (
          <ModalEntry
            pageNumber={modalNumber}
            inputAgencyStatus={inputAgencyStatus?.status}
            isClose={() => {
              setModal(false);
            }}
            fileData={fileData}
            isOpen={modal}
            getInputAgency={getInputAgency}
          />
        )}
        <Modal
          isOpen={modalDelete}
          isClose={() => setModalDelete(false)}
          className="rounded-modalContainer"
        >
          <div className="w-624 h-256 sm:w-288 sm:h-160 p-40 flex justify-center items-center">
            <div>
              <p className="mbx-typography--body_1">
                {deleteType === 'resume' ? '履歴書' : '職務経歴書'}のファイルを削除しますか？
              </p>
              <div className="flex justify-center mt-24 sm:mt-24 sm:p-0 gap-16">
                <div className="w-146 sm:w-114">
                  <BaseButton
                    theme={'secondary'}
                    size="m"
                    className="w-full"
                    onClick={() => setModalDelete(false)}
                  >
                    キャンセル
                  </BaseButton>
                </div>
                <div className="w-146 sm:w-114">
                  <BaseButton
                    theme={'primary'}
                    size="m"
                    className="w-full"
                    onClick={() => deleteDocument()}
                  >
                    削除する
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </Wrapper>
  );
};
